import React from "react";
import SeoLandingLayout from "./SeoLandingLayout";
import { BookOpen, Sparkles, Map as MapIcon, Users, Flame, Feather } from "lucide-react";
import { NARRATOR_LIST } from "@/data/models";

const FEATURES = [
  { icon: BookOpen, title: "Become the main character", body: "Every chapter is written around you — your choices, your character, the way your hero talks. Not a chatbot; a novel that answers back." },
  { icon: MapIcon, title: "Illustrated story worlds", body: "Automatic scene art on every major beat. Explore curated realms or invent your own from a plain-English prompt." },
  { icon: Sparkles, title: "Memory Codex", body: "Named characters, threads, milestones and inventory changes are tracked and fed back to the narrator to improve continuity across long sagas." },
  { icon: Users, title: "Switchable AI narrators", body: `Swap between ${NARRATOR_LIST} mid-book while the Codex carries story details forward.` },
  { icon: Feather, title: "Prose that reads like a novel", body: "Literary voice, real sensory detail, dialogue in the character's cadence. Ambience-first, mechanics-optional." },
  { icon: Flame, title: "Free daily turns", body: "10 turns/day on the free Wanderer tier — no card. Paid tiers unlock more turns and premium narrators." },
];

const FAQ = [
  {
    q: "What is an AI novel game?",
    a: "An AI novel game is an interactive fiction experience where an AI narrator writes each chapter around your choices and actions. TalesTalesTales is an AI novel game where the reader becomes the protagonist — you type what you do, the AI writes the scene, illustrates it, and offers you the next set of choices. Every saga is unique because the story is generated turn by turn."
  },
  {
    q: "How is it different from a chatbot?",
    a: "A chatbot answers questions; TalesTalesTales writes a novel with you. Every scene is illustrated, your hero has skills and inventory tracked across chapters, the Memory Codex feeds important details from chapter one back to the narrator when you reach chapter forty, and you can switch AI narrators mid-story while the Codex carries story details forward."
  },
  {
    q: "Can I keep the chapters I write?",
    a: "Yes. Every saga saves automatically. You can return any time and continue the same book, roll back a turn, or publish your custom world to the community library so other players can read it."
  },
  {
    q: "Do I need to write well?",
    a: "No. Type what you want to do in your own words — 'I sneak past the guards', 'I ask her about the shipment', 'I draw my blade'. The AI handles the prose. Many players find their writing improves just by reading the narrator's cadence."
  },
];

export default function AiNovelGame() {
  return (
    <SeoLandingLayout
      testId="seo-ai-novel-game-page"
      path="/ai-novel-game"
      title="AI Novel Game | TalesTalesTales"
      description="TalesTalesTales is an AI novel game where you become the main character in a branching illustrated story. Create a hero, explore worlds, and let AI narrators write each chapter around your choices."
      eyebrow="AI Novel Game"
      h1="AI Novel Game"
      h1Accent="where you become the main character"
      lede="TalesTalesTales is an interactive AI Novel. You create a character, enter an illustrated story world, and shape every chapter with your choices while an AI narrator writes the prose in literary voice. The Memory Codex tracks important story details to improve continuity across long sagas."
      body={(
        <>
          <p>
            An <strong>AI novel game</strong> is more than a chatbot with a fantasy skin. It&rsquo;s a
            reader-facing interactive fiction experience where the AI narrator commits to the story,
            keeps track of the world, and treats <em>you</em> as the protagonist. TalesTalesTales was
            built specifically for that use case: the writing is the point, and the mechanics stay quiet
            unless you turn them up.
          </p>
          <p>
            The Memory Codex is what helps long books stay consistent. Named characters, threads,
            milestones, and inventory changes are recorded turn by turn and fed back to the narrator.
            When you return in chapter forty, the narrator has access to the details it tracked from
            chapter three. Switch narrators &mdash; Claude Sonnet 4.5, Claude Haiku 4.5, GPT-5, or
            Gemini 2.5 Pro &mdash; and the codex carries those details across the swap. This improves
            continuity; it does not guarantee perfect recall over very long sagas.
          </p>
          <p>
            You can play it as a pure novel: dice silent, prose only, choices via a text box. You can
            layer in <strong>light RPG elements</strong> (dice, skills, equipment, inventory)
            when you want a chapter driven by a roll. Most players end up somewhere in the middle
            &mdash; an illustrated novel with the occasional roll.
          </p>
          <p>
            Every scene is auto-illustrated by an image model tuned for the setting, so you always have
            a picture of where you are. Sagas save automatically. You can publish your custom worlds to
            the community library and read what other people wrote in yours.
          </p>
        </>
      )}
      features={FEATURES}
      faq={FAQ}
    />
  );
}
